import { useState, useCallback } from 'react';
import { Upload, FileSpreadsheet, FileText, Check, AlertCircle, Trash2 } from 'lucide-react';
import type {
  ParseExcelResponse,
  ParseWordResponse,
  ParsedExpenseRow,
  ImportRequest,
  ImportResult,
  ImportExpenseItem,
  ImportTimelineSection,
  ImportChecklistEntry,
  ImportKibuddimSection,
} from '@peles/shared';
import { useParseExcel, useParseWord, useExecuteImport } from '../hooks/useDataImport';
import { SearchableSelect } from '../components/common/SearchableSelect';

type WizardStep = 'upload' | 'preview' | 'map' | 'review' | 'result';

interface ParsedFile {
  name: string;
  type: 'excel' | 'word';
  excelData?: ParseExcelResponse;
  wordData?: ParseWordResponse;
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export function DataImportPage() {
  const [step, setStep] = useState<WizardStep>('upload');
  const [parsedFiles, setParsedFiles] = useState<ParsedFile[]>([]);
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [useExistingEvent, setUseExistingEvent] = useState(false);
  const [existingEventId, setExistingEventId] = useState('');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const parseExcel = useParseExcel();
  const parseWord = useParseWord();
  const executeImport = useExecuteImport();

  // ── Expense rows from all parsed Excel files
  const allExpenseRows: ParsedExpenseRow[] = parsedFiles.flatMap(
    (f) => f.excelData?.expenses.rows ?? [],
  );

  // ── Selected expense rows (toggle)
  const [selectedExpenseIndices, setSelectedExpenseIndices] = useState<Set<number>>(new Set());

  const toggleExpenseRow = useCallback((idx: number) => {
    setSelectedExpenseIndices((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const selectAllExpenses = useCallback(() => {
    setSelectedExpenseIndices(new Set(allExpenseRows.map((_, i) => i)));
  }, [allExpenseRows]);

  // ── File upload handler
  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      setError(null);
      const fileArray = Array.from(files);

      for (const file of fileArray) {
        const ext = file.name.split('.').pop()?.toLowerCase();
        try {
          if (ext === 'xlsx' || ext === 'xls') {
            const data = await parseExcel.mutateAsync(file);
            setParsedFiles((prev) => [
              ...prev,
              { name: file.name, type: 'excel', excelData: data },
            ]);
          } else if (ext === 'docx' || ext === 'doc') {
            const data = await parseWord.mutateAsync(file);
            setParsedFiles((prev) => [...prev, { name: file.name, type: 'word', wordData: data }]);
          } else {
            setError(`Unsupported file type: .${ext}`);
          }
        } catch (err) {
          setError(
            `Failed to parse ${file.name}: ${err instanceof Error ? err.message : 'Unknown error'}`,
          );
        }
      }
    },
    [parseExcel, parseWord],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFiles(e.target.files);
      }
    },
    [handleFiles],
  );

  const removeFile = useCallback((idx: number) => {
    setParsedFiles((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  // ── Build import request from parsed data
  const buildImportRequest = useCallback((): ImportRequest => {
    const expenses: ImportExpenseItem[] = [];
    allExpenseRows.forEach((row, idx) => {
      if (!selectedExpenseIndices.has(idx)) return;
      if (!row.category || row.price === null) return;
      expenses.push({
        category: row.category,
        item: row.item,
        vendor: row.vendor,
        price: row.price,
        status: row.status,
        paymentMethod: row.paymentMethod,
        paid: row.paid,
        dueDate: row.dueDate,
        comments: row.comments,
      });
    });

    const timelineSections: ImportTimelineSection[] = [];
    const checklistEntries: ImportChecklistEntry[] = [];
    const kibuddimSections: ImportKibuddimSection[] = [];

    for (const f of parsedFiles) {
      if (!f.wordData) continue;
      if (f.wordData.timeline) {
        for (const sec of f.wordData.timeline.sections) {
          timelineSections.push({
            name: sec.name,
            entries: sec.entries.map((e) => ({
              time: e.time,
              title: e.title,
              location: e.location,
            })),
          });
        }
      }
      if (f.wordData.checklist) {
        for (const entry of f.wordData.checklist.entries) {
          checklistEntries.push({
            time: entry.time,
            task: entry.task,
            location: entry.location,
          });
        }
      }
      if (f.wordData.kibuddim) {
        for (const sec of f.wordData.kibuddim.sections) {
          kibuddimSections.push({
            name: sec.name,
            roles: sec.roles.map((r) => ({
              roleName: r.roleName,
              assignee: r.assignee,
            })),
          });
        }
      }
    }

    return {
      eventId: useExistingEvent && existingEventId ? existingEventId : undefined,
      eventName: eventName || 'Imported Event',
      eventDate: eventDate || undefined,
      expenses: expenses.length > 0 ? expenses : undefined,
      timelineSections: timelineSections.length > 0 ? timelineSections : undefined,
      checklistEntries: checklistEntries.length > 0 ? checklistEntries : undefined,
      kibuddimSections: kibuddimSections.length > 0 ? kibuddimSections : undefined,
    };
  }, [
    allExpenseRows,
    selectedExpenseIndices,
    parsedFiles,
    eventName,
    eventDate,
    useExistingEvent,
    existingEventId,
  ]);

  // ── Execute import
  const handleExecuteImport = useCallback(async () => {
    setError(null);
    try {
      const request = buildImportRequest();
      const result = await executeImport.mutateAsync(request);
      setImportResult(result);
      setStep('result');
    } catch (err) {
      setError(`Import failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [buildImportRequest, executeImport]);

  // ── Step navigation
  const canProceedToPreview = parsedFiles.length > 0;
  const canProceedToMap = canProceedToPreview;
  const canProceedToReview = useExistingEvent ? !!existingEventId : eventName.trim().length > 0;

  const steps: { id: WizardStep; label: string; num: number }[] = [
    { id: 'upload', label: 'Upload', num: 1 },
    { id: 'preview', label: 'Preview', num: 2 },
    { id: 'map', label: 'Map Fields', num: 3 },
    { id: 'review', label: 'Review', num: 4 },
    { id: 'result', label: 'Results', num: 5 },
  ];

  const isLoading = parseExcel.isPending || parseWord.isPending || executeImport.isPending;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Data Import</h1>

      {/* Step indicator */}
      <div className="flex items-center gap-2" data-testid="step-indicator">
        {steps.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2">
            <button
              onClick={() => {
                if (s.id !== 'result') setStep(s.id);
              }}
              className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                step === s.id ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}
              data-testid={`step-${s.id}`}
            >
              {s.num}. {s.label}
            </button>
            {i < steps.length - 1 && <span className="text-gray-400">&rarr;</span>}
          </div>
        ))}
      </div>

      {/* Error banner */}
      {error && (
        <div
          className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md"
          data-testid="error-banner"
        >
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Step content */}
      {step === 'upload' && (
        <UploadStep
          parsedFiles={parsedFiles}
          onDrop={handleDrop}
          onFileInput={handleFileInput}
          onRemoveFile={removeFile}
          isLoading={isLoading}
          onNext={() => setStep('preview')}
          canProceed={canProceedToPreview}
        />
      )}

      {step === 'preview' && (
        <PreviewStep
          parsedFiles={parsedFiles}
          onBack={() => setStep('upload')}
          onNext={() => setStep('map')}
          canProceed={canProceedToMap}
        />
      )}

      {step === 'map' && (
        <MapStep
          allExpenseRows={allExpenseRows}
          selectedExpenseIndices={selectedExpenseIndices}
          onToggleExpense={toggleExpenseRow}
          onSelectAll={selectAllExpenses}
          eventName={eventName}
          onEventNameChange={setEventName}
          eventDate={eventDate}
          onEventDateChange={setEventDate}
          useExistingEvent={useExistingEvent}
          onUseExistingEventChange={setUseExistingEvent}
          existingEventId={existingEventId}
          onExistingEventIdChange={setExistingEventId}
          onBack={() => setStep('preview')}
          onNext={() => setStep('review')}
          canProceed={canProceedToReview}
        />
      )}

      {step === 'review' && (
        <ReviewStep
          importRequest={buildImportRequest()}
          onBack={() => setStep('map')}
          onExecute={handleExecuteImport}
          isLoading={executeImport.isPending}
        />
      )}

      {step === 'result' && importResult && <ResultStep result={importResult} />}
    </div>
  );
}

// ─── Step Components ────────────────────────────────────────────────────────

interface UploadStepProps {
  parsedFiles: ParsedFile[];
  onDrop: (e: React.DragEvent) => void;
  onFileInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (idx: number) => void;
  isLoading: boolean;
  onNext: () => void;
  canProceed: boolean;
}

export function UploadStep({
  parsedFiles,
  onDrop,
  onFileInput,
  onRemoveFile,
  isLoading,
  onNext,
  canProceed,
}: UploadStepProps) {
  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-400 transition-colors"
        data-testid="drop-zone"
      >
        <Upload className="mx-auto mb-3 text-gray-400" size={40} />
        <p className="text-gray-600 mb-2">Drag & drop Excel (.xlsx) or Word (.docx) files here</p>
        <label className="inline-block px-4 py-2 bg-primary-600 text-white rounded-md cursor-pointer hover:bg-primary-700">
          Browse Files
          <input
            type="file"
            className="hidden"
            multiple
            accept=".xlsx,.xls,.docx,.doc"
            onChange={onFileInput}
            data-testid="file-input"
          />
        </label>
      </div>

      {isLoading && (
        <div className="text-center text-gray-500" data-testid="loading">
          Parsing files...
        </div>
      )}

      {/* Parsed files list */}
      {parsedFiles.length > 0 && (
        <div className="space-y-2" data-testid="parsed-files-list">
          <h3 className="font-medium text-gray-700">Parsed Files</h3>
          {parsedFiles.map((f, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-md border"
            >
              <div className="flex items-center gap-2">
                {f.type === 'excel' ? (
                  <FileSpreadsheet size={18} className="text-green-600" />
                ) : (
                  <FileText size={18} className="text-blue-600" />
                )}
                <span className="text-sm font-medium">{f.name}</span>
                <span className="text-xs text-gray-500">
                  {f.type === 'excel'
                    ? `${f.excelData?.expenses.rows.length ?? 0} expense rows`
                    : (f.wordData?.documentType ?? 'unknown')}
                </span>
              </div>
              <button
                onClick={() => onRemoveFile(idx)}
                className="text-gray-400 hover:text-red-500"
                data-testid={`remove-file-${idx}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={onNext}
          disabled={!canProceed}
          className="px-4 py-2 bg-primary-600 text-white rounded-md disabled:opacity-50"
          data-testid="next-btn"
        >
          Next: Preview Data
        </button>
      </div>
    </div>
  );
}

interface PreviewStepProps {
  parsedFiles: ParsedFile[];
  onBack: () => void;
  onNext: () => void;
  canProceed: boolean;
}

export function PreviewStep({ parsedFiles, onBack, onNext, canProceed }: PreviewStepProps) {
  return (
    <div className="space-y-4">
      {parsedFiles.map((f, idx) => (
        <div key={idx} className="border rounded-lg p-4">
          <h3 className="font-medium mb-2">
            {f.type === 'excel' ? (
              <FileSpreadsheet size={16} className="inline text-green-600 mr-1" />
            ) : (
              <FileText size={16} className="inline text-blue-600 mr-1" />
            )}
            {f.name}
          </h3>

          {f.excelData && (
            <div className="overflow-x-auto" data-testid="expense-preview">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-2 py-1 text-left">Category</th>
                    <th className="px-2 py-1 text-left">Item</th>
                    <th className="px-2 py-1 text-left">Vendor</th>
                    <th className="px-2 py-1 text-right">Price</th>
                    <th className="px-2 py-1 text-left">Status</th>
                    <th className="px-2 py-1 text-left">Pay Method</th>
                  </tr>
                </thead>
                <tbody>
                  {f.excelData.expenses.rows.slice(0, 20).map((row, ri) => (
                    <tr key={ri} className="border-t">
                      <td className="px-2 py-1">{row.category}</td>
                      <td className="px-2 py-1">{row.item}</td>
                      <td className="px-2 py-1">{row.vendor}</td>
                      <td className="px-2 py-1 text-right">
                        {row.price != null ? `$${row.price.toLocaleString()}` : '-'}
                      </td>
                      <td className="px-2 py-1">{row.status}</td>
                      <td className="px-2 py-1">{row.paymentMethod}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {f.excelData.expenses.rows.length > 20 && (
                <p className="text-xs text-gray-500 mt-1">
                  Showing 20 of {f.excelData.expenses.rows.length} rows
                </p>
              )}
            </div>
          )}

          {f.wordData && (
            <div data-testid="word-preview">
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                {f.wordData.documentType}
              </span>
              {f.wordData.timeline && (
                <div className="mt-2">
                  <p className="text-sm font-medium">{f.wordData.timeline.title}</p>
                  <p className="text-xs text-gray-500">
                    {f.wordData.timeline.sections.length} sections,{' '}
                    {f.wordData.timeline.sections.reduce((sum, s) => sum + s.entries.length, 0)}{' '}
                    entries
                  </p>
                </div>
              )}
              {f.wordData.checklist && (
                <div className="mt-2">
                  <p className="text-sm font-medium">{f.wordData.checklist.title}</p>
                  <p className="text-xs text-gray-500">
                    {f.wordData.checklist.entries.length} checklist items
                  </p>
                </div>
              )}
              {f.wordData.kibuddim && (
                <div className="mt-2">
                  <p className="text-sm font-medium">{f.wordData.kibuddim.title}</p>
                  <p className="text-xs text-gray-500">
                    {f.wordData.kibuddim.sections.length} sections,{' '}
                    {f.wordData.kibuddim.sections.reduce((sum, s) => sum + s.roles.length, 0)} roles
                  </p>
                </div>
              )}
              {f.wordData.guestList && (
                <div className="mt-2">
                  <p className="text-sm font-medium">{f.wordData.guestList.title}</p>
                  <p className="text-xs text-gray-500">
                    {f.wordData.guestList.sections.length} sections
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      <div className="flex justify-between">
        <button onClick={onBack} className="px-4 py-2 border rounded-md" data-testid="back-btn">
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed}
          className="px-4 py-2 bg-primary-600 text-white rounded-md disabled:opacity-50"
          data-testid="next-btn"
        >
          Next: Map Fields
        </button>
      </div>
    </div>
  );
}

interface MapStepProps {
  allExpenseRows: ParsedExpenseRow[];
  selectedExpenseIndices: Set<number>;
  onToggleExpense: (idx: number) => void;
  onSelectAll: () => void;
  eventName: string;
  onEventNameChange: (v: string) => void;
  eventDate: string;
  onEventDateChange: (v: string) => void;
  useExistingEvent: boolean;
  onUseExistingEventChange: (v: boolean) => void;
  existingEventId: string;
  onExistingEventIdChange: (v: string) => void;
  onBack: () => void;
  onNext: () => void;
  canProceed: boolean;
}

export function MapStep({
  allExpenseRows,
  selectedExpenseIndices,
  onToggleExpense,
  onSelectAll,
  eventName,
  onEventNameChange,
  eventDate,
  onEventDateChange,
  useExistingEvent,
  onUseExistingEventChange,
  existingEventId,
  onExistingEventIdChange,
  onBack,
  onNext,
  canProceed,
}: MapStepProps) {
  return (
    <div className="space-y-4">
      {/* Toggle: create new event or use existing */}
      <div className="flex items-center gap-4 mb-2">
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="radio"
            name="event-mode"
            checked={!useExistingEvent}
            onChange={() => onUseExistingEventChange(false)}
            className="text-primary-600 focus:ring-primary-500"
          />
          Create new event
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="radio"
            name="event-mode"
            checked={useExistingEvent}
            onChange={() => onUseExistingEventChange(true)}
            className="text-primary-600 focus:ring-primary-500"
            data-testid="use-existing-event-radio"
          />
          Use existing event
        </label>
      </div>

      {useExistingEvent ? (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Select Event *</label>
          <SearchableSelect
            entityType="event"
            value={existingEventId}
            onChange={(v) => onExistingEventIdChange(typeof v === 'string' ? v : v[0] || '')}
            placeholder="Search for an event..."
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Name *</label>
            <input
              type="text"
              value={eventName}
              onChange={(e) => onEventNameChange(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
              placeholder="e.g. Shagalov Wedding"
              data-testid="event-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Date</label>
            <input
              type="date"
              value={eventDate}
              onChange={(e) => onEventDateChange(e.target.value)}
              className="w-full px-3 py-2 border rounded-md"
              data-testid="event-date-input"
            />
          </div>
        </div>
      )}

      {allExpenseRows.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium">
              Expense Items ({selectedExpenseIndices.size}/{allExpenseRows.length} selected)
            </h3>
            <button
              onClick={onSelectAll}
              className="text-sm text-primary-600 hover:underline"
              data-testid="select-all-btn"
            >
              Select All
            </button>
          </div>
          <div className="overflow-x-auto border rounded-lg" data-testid="expense-table">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-2 py-1 w-8"></th>
                  <th className="px-2 py-1 text-left">Category</th>
                  <th className="px-2 py-1 text-left">Item</th>
                  <th className="px-2 py-1 text-left">Vendor</th>
                  <th className="px-2 py-1 text-right">Price</th>
                  <th className="px-2 py-1 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {allExpenseRows.map((row, idx) => (
                  <tr
                    key={idx}
                    className={`border-t cursor-pointer ${
                      selectedExpenseIndices.has(idx) ? 'bg-primary-50' : ''
                    }`}
                    onClick={() => onToggleExpense(idx)}
                  >
                    <td className="px-2 py-1 text-center">
                      <input
                        type="checkbox"
                        checked={selectedExpenseIndices.has(idx)}
                        onChange={() => onToggleExpense(idx)}
                        data-testid={`expense-check-${idx}`}
                      />
                    </td>
                    <td className="px-2 py-1">{row.category}</td>
                    <td className="px-2 py-1">{row.item}</td>
                    <td className="px-2 py-1">{row.vendor}</td>
                    <td className="px-2 py-1 text-right">
                      {row.price != null ? `$${row.price.toLocaleString()}` : '-'}
                    </td>
                    <td className="px-2 py-1">{row.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex justify-between">
        <button onClick={onBack} className="px-4 py-2 border rounded-md" data-testid="back-btn">
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed}
          className="px-4 py-2 bg-primary-600 text-white rounded-md disabled:opacity-50"
          data-testid="next-btn"
        >
          Next: Review
        </button>
      </div>
    </div>
  );
}

interface ReviewStepProps {
  importRequest: ImportRequest;
  onBack: () => void;
  onExecute: () => void;
  isLoading: boolean;
}

export function ReviewStep({ importRequest, onBack, onExecute, isLoading }: ReviewStepProps) {
  const expenseCount = importRequest.expenses?.length ?? 0;
  const timelineCount =
    importRequest.timelineSections?.reduce((sum, s) => sum + s.entries.length, 0) ?? 0;
  const checklistCount = importRequest.checklistEntries?.length ?? 0;
  const kibuddimCount =
    importRequest.kibuddimSections?.reduce((sum, s) => sum + s.roles.length, 0) ?? 0;

  return (
    <div className="space-y-4">
      <div className="border rounded-lg p-4" data-testid="review-summary">
        <h3 className="font-medium mb-3">Import Summary</h3>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <span className="text-gray-600">Event Name:</span>
          <span className="font-medium">{importRequest.eventName}</span>
          <span className="text-gray-600">Event Date:</span>
          <span className="font-medium">{importRequest.eventDate || 'Not set'}</span>
          <span className="text-gray-600">Expense Items:</span>
          <span className="font-medium">{expenseCount}</span>
          <span className="text-gray-600">Timeline Entries:</span>
          <span className="font-medium">{timelineCount}</span>
          <span className="text-gray-600">Checklist Items:</span>
          <span className="font-medium">{checklistCount}</span>
          <span className="text-gray-600">Kibuddim Roles:</span>
          <span className="font-medium">{kibuddimCount}</span>
        </div>
      </div>

      <div className="flex justify-between">
        <button onClick={onBack} className="px-4 py-2 border rounded-md" data-testid="back-btn">
          Back
        </button>
        <button
          onClick={onExecute}
          disabled={isLoading}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
          data-testid="execute-btn"
        >
          {isLoading ? 'Importing...' : 'Execute Import'}
        </button>
      </div>
    </div>
  );
}

interface ResultStepProps {
  result: ImportResult;
}

export function ResultStep({ result }: ResultStepProps) {
  return (
    <div className="text-center space-y-4" data-testid="import-result">
      <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
        <Check className="text-green-600" size={32} />
      </div>
      <h2 className="text-xl font-bold">Import Complete</h2>
      <p className="text-gray-600">
        Event &quot;{result.eventName}&quot; has been created successfully.
      </p>
      <div className="inline-block border rounded-lg p-4 text-left text-sm">
        <div className="grid grid-cols-2 gap-2">
          <span className="text-gray-600">Expense Items:</span>
          <span className="font-medium">{result.counts.expenseItems}</span>
          <span className="text-gray-600">Timeline Entries:</span>
          <span className="font-medium">{result.counts.timelineEntries}</span>
          <span className="text-gray-600">Checklist Items:</span>
          <span className="font-medium">{result.counts.checklistItems}</span>
          <span className="text-gray-600">Kibuddim Roles:</span>
          <span className="font-medium">{result.counts.kibuddimRoles}</span>
        </div>
      </div>
    </div>
  );
}
